<template>
  <div
    :class="{
      'location-input-snippet': true,
      'location-input-snippet--error': props.showValidation && (_collabLocation.title || _collabLocation.externalId) && (_collabLocation.latitude === null || _collabLocation.longitude === null),
      'location-input-snippet--align-items-center': snippetState === SnippetStates.ShortForm,
      'location-input-snippet--small-vertical-paddings': snippetState === SnippetStates.ShortForm,
      'location-input-snippet--big-vertical-paddings-and-gray-bg': isEditMode || snippetState === SnippetStates.FullForm,
      'location-input-snippet--disabled': props.isSnippetDisabled,
    }"
    :data-index="props.index"
    v-click-outside="
      () => {
        if (snippetState === SnippetStates.ShortForm) {
          isAutocompleteInputFocused = false;
        }
      }
    "
  >
    <!-- Column left -->
    <div
      :class="{
        'location-input-snippet__column-left': true,
        'location-input-snippet__column-left--disabled': isEditMode || !_collabLocation.title,
        'location-input-snippet__column-left--smaller-height': snippetState === SnippetStates.ShortSummary,
      }"
      :data-index="props.index"
    >
      <IconEmbedded
        :class="{
          'location-input-snippet__drag-n-drop-handle': true,
          'location-input-snippet__drag-n-drop-handle--disabled': isEditMode || !_collabLocation.title,
        }"
        name="drag-n-drop-handle-2-lines"
        :size="26"
        color="rgba(0, 0, 0, 0.3)"
      />

      <div class="location-input-snippet__order-number">{{ props.index + 1 }}</div>
    </div>
    <!-- / Column left -->

    <!-- Column right -->
    <div
      :class="{
        'location-input-snippet__column-right': true,
        'location-input-snippet__column-right--limited-width': [SnippetStates.ShortForm, SnippetStates.FullForm].includes(snippetState),
        'location-input-snippet__column-right--smaller-height': snippetState === SnippetStates.ShortSummary,
      }"
    >
      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- Short form -->
      <div v-if="snippetState === SnippetStates.ShortForm" class="short-form location-input-snippet__short-form">
        <GMapAutocomplete
          class="global-text-input short-form__location-input"
          ref="autocompleteInput"
          @place_changed="event => googlePlaceSelected(event)"
          @input="event => (_collabLocation.title = event.target.value)"
          @error="autoCompleteError"
          :value="_collabLocation.title"
          :options="{
            fields: ['name', 'place_id', 'address_components', 'formatted_address', 'geometry', 'website', 'opening_hours'],
          }"
          @focus="
            () => {
              emit('focusOnAnyField');
              isAutocompleteInputFocused = true;
            }
          "
        />

        <SrpButton v-if="isEditMode" class="short-form__add-button" fill="outlined" @click="addWithoutGooglePlace" :isDisabled="!_collabLocation.title">Add</SrpButton>
      </div>
      <!-- / Short form -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->

      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- Short summary -->
      <div v-if="snippetState === SnippetStates.ShortSummary" class="short-summary location-input-snippet__short-summary">
        <div class="short-summary__text-content">
          <div class="short-summary__name">
            <span v-if="isCollabLocationUsedInCollab" style="margin: 0 -3px 0 -9px; font-size: 20px; color: sandybrown; vertical-align: bottom">*</span>
            {{ _collabLocation.title }}
          </div>

          <div v-if="_collabLocation.visitPlanNotes || _collabLocation.visitDay" class="short-summary__note-and-visit-day">
            <div v-if="_collabLocation.visitDay" class="short-summary__visit-day">{{ moment(_collabLocation.visitDay).format("ddd, MMM Do") }}</div>

            <div v-if="_collabLocation.visitPlanNotes" class="short-summary__note">Note: {{ _collabLocation.visitPlanNotes }}</div>
          </div>
        </div>

        <LinkWithIcon class="short-summary__edit-button" color="teal" @click="snippetState = SnippetStates.FullForm">
          <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
        </LinkWithIcon>
      </div>
      <!-- / Short summary -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->

      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- Full form -->
      <div v-if="snippetState === SnippetStates.FullForm" class="full-form location-input-snippet__full-form">
        <GMapAutocomplete
          class="global-text-input full-form__place-name-input"
          ref="autocompleteInput"
          @place_changed="event => googlePlaceSelected(event)"
          @input="event => (_collabLocation.title = event.target.value)"
          @error="autoCompleteError"
          :value="_collabLocation.title"
          :options="{
            fields: ['name', 'place_id', 'address_components', 'formatted_address', 'geometry', 'website', 'opening_hours'],
          }"
          @focus="
            () => {
              emit('focusOnAnyField');
              isAutocompleteInputFocused = true;
            }
          "
          @blur="
            () => {
              // snippetState = _collabLocation.title ? SnippetStates.ShortSummary : SnippetStates.ShortForm;
              isAutocompleteInputFocused = false;
            }
          "
        />

        <!-- Address row -->
        <div v-if="!isAddressInputVisible" class="address-row full-form__address-row">
          <div class="address-row__address"><b>Address:</b> {{ _collabLocation.address || "No address provided" }}</div>

          <LinkWithIcon class="address-row__change-link" color="blue" :isDottedUnderline="true" @click="isAddressInputVisible = true">
            <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
            <span>change</span>
          </LinkWithIcon>
        </div>
        <!-- / Address row -->

        <input
          v-else
          class="global-text-input full-form__address-input"
          placeholder="Enter an address"
          v-model="_collabLocation.address"
          @focus="isAddressInputFocused = true"
          @blur="isAddressInputFocused = false"
        />

        <NoteWithIcon v-if="isSetThePinNoteVisible" class="full-form__set-the-pin-warning" size="tiny">
          <template #icon><IconEmbedded name="info-simple_4" :size="17" /></template>
          <template #text><span>Click on the map to set the pin</span></template>
        </NoteWithIcon>

        <!-- Visit day row -->
        <div class="visit-day-row full-form__visit-day-row">
          <div class="visit-day-row__title">Visiting:</div>

          <input class="global-text-input visit-day-row__input-date" type="date" :min="new Date().toISOString().split('T')[0]" v-model="_collabLocation.visitDay" />

          <div
            v-if="_collabLocation.openHours"
            :class="{
              'visit-day-row__working-hours': true,
              'visit-day-row__working-hours--red-and-bold': openHoursText.toLowerCase() === 'closed',
            }"
          >
            {{ openHoursText }}
          </div>
        </div>
        <!-- / Visit day row -->

        <!-- Note row -->
        <div class="note-row full-form__note-row">
          <div class="note-row__title">Note:</div>

          <input class="global-text-input note-row__text-input" v-model="_collabLocation.visitPlanNotes" />
        </div>
        <!-- / Note row -->

        <!-- Buttons row -->
        <div class="buttons-row full-form__buttons-row">
          <div class="buttons-row__side-left">
            <SrpButton class="buttons-row__button" size="small" @click="save" :isDisabled="isSetThePinNoteVisible">
              <template #icon><IconEmbedded name="check_4" :size="17" /></template>
              Save
            </SrpButton>

            <SrpButton class="buttons-row__button" size="small" fill="outlined" color="gray" @click="cancelEditing"> Cancel </SrpButton>
          </div>

          <div class="buttons-row__side-right">
            <TooltipWrapper v-if="props.isCollabLocationUsedInCollab" text="This location can't be deleted because it's used in the Collab" placement="top" :width="200">
              <template #hoverableElement>
                <SrpButton v-if="screenSize === 'mobile'" class="buttons-row__button" size="small" fill="outlined" color="red" sidePaddings="small" :isDisabled="true">
                  <template #icon><IconEmbedded name="trashcan-alt_2-5" :size="17" /></template>
                </SrpButton>

                <SrpButton v-else class="buttons-row__button" size="small" fill="outlined" color="red" :isDisabled="true">
                  <template #icon><IconEmbedded name="trashcan-alt_2-5" :size="17" /></template>
                  Delete
                </SrpButton>
              </template>
            </TooltipWrapper>

            <template v-else>
              <SrpButton
                v-if="screenSize === 'mobile'"
                class="buttons-row__button"
                size="small"
                fill="outlined"
                color="red"
                sidePaddings="small"
                @click="deleteLocation"
                :isDisabled="props.isCollabLocationUsedInCollab"
              >
                <template #icon><IconEmbedded name="trashcan-alt_2-5" :size="17" /></template>
              </SrpButton>

              <SrpButton v-else class="buttons-row__button" size="small" fill="outlined" color="red" @click="deleteLocation" :isDisabled="props.isCollabLocationUsedInCollab">
                <template #icon><IconEmbedded name="trashcan-alt_2-5" :size="17" /></template>
                Delete
              </SrpButton>
            </template>
          </div>
        </div>
        <!-- / Buttons row -->
      </div>
      <!-- / Full form -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->
      <!-- =================================================================== -->
    </div>
    <!-- / Column right -->
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, inject, watch, computed, onMounted, nextTick } from "vue";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import GooglePlaceConverter from "@logic/GooglePlaceConverter";
import { daysOfWeek, dayOfWeekIndexes } from "@contracts/daysOfWeek";
import moment from "moment";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";

// Types
import { ScreenSize } from "@contracts/screenSize";
import { CollabLocation } from "@contracts/collabLocations";
import { Location } from "@contracts/location";

// Global variables
const screenSize = inject("screenSize", null) as Ref<ScreenSize>;

const props = withDefaults(
  defineProps<{
    index: number;
    isSnippetDisabled: boolean;
    showValidation: boolean;
    collabLocation: CollabLocation | null;
    isCollabLocationUsedInCollab?: boolean;
    saveLocationFunc: (collabLocationId: string) => Promise<void>;
  }>(),
  {
    index: -1,
    isSnippetDisabled: false,
    showValidation: false,
    collabLocation: null,
    isCollabLocationUsedInCollab: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    saveLocationFunc: async () => {},
  }
);

const emit = defineEmits<{
  (e: "focusOnAnyField"): void;
  (e: "toggleEditMode", isEditMode: boolean): void;
  (e: "googlePlaceSelected", value: CollabLocation): void;
  (e: "update:collabLocation", value: CollabLocation): void;
  (e: "showSetThePinNote", value: boolean): void;
  (e: "deleteLocation", index: number): void;
}>();

// DOM refs ===================================================================
const autocompleteInput = ref<any>(null);

// Snippet state ==============================================================
enum SnippetStates {
  ShortForm,
  ShortSummary,
  FullForm,
}

const snippetState = ref<SnippetStates>(SnippetStates.ShortSummary);

onMounted(() => {
  if (!props.collabLocation.title) {
    snippetState.value = SnippetStates.ShortForm;
  }
});

// Autocomplete and address fields focus status ===============================
const isAutocompleteInputFocused = ref<boolean>(false);
const isAddressInputFocused = ref<boolean>(false);

// CollabLocation local copy ==================================================
const _collabLocation = ref<CollabLocation>({ ...props.collabLocation });

watch(
  _collabLocation,
  () => {
    emit("update:collabLocation", _collabLocation.value);
  },
  { deep: true }
);

// Is address user edited =====================================================
// address value comes from the Google Place API by default, but the user can alter it if he wants
const isAddressInputVisible = ref<boolean>(false);

// Emit "isEditMode" state ====================================================
const isEditMode = computed<boolean>(() => {
  return Boolean(isAutocompleteInputFocused.value || isAddressInputFocused.value || snippetState.value === SnippetStates.FullForm);
});

watch(isEditMode, () => emit("toggleEditMode", isEditMode.value));

// Cancel editing =============================================================
const collabLocationUntouchedCopy = ref<CollabLocation | null>(null);

watch(snippetState, () => {
  if (snippetState.value === SnippetStates.FullForm) {
    collabLocationUntouchedCopy.value = JSON.parse(JSON.stringify(props.collabLocation));
  }
});

function cancelEditing() {
  _collabLocation.value = { ...collabLocationUntouchedCopy.value };

  emit("update:collabLocation", _collabLocation.value);

  isAutocompleteInputFocused.value = false;
  isAddressInputFocused.value = false;

  isAddressInputVisible.value = false;

  if (props.collabLocation?.title) {
    snippetState.value = SnippetStates.ShortSummary;
  } else {
    snippetState.value = SnippetStates.ShortForm;
  }
}

// Google place selected ======================================================
async function googlePlaceSelected(place: google.maps.places.PlaceResult) {
  getGlobalRemoteLogger().info(`Collab LocationInput googlePlaceSelected: ${place?.name} ${place?.place_id}`);
  GooglePlaceConverter.setItineraryFieldsFromPlace(place, _collabLocation.value);
  emit("googlePlaceSelected", _collabLocation.value);

  snippetState.value = SnippetStates.FullForm;
}

function autoCompleteError(error) {
  // Log the error
  console.error("Google Maps Autocomplete Error:", error);
  getGlobalRemoteLogger().error(`--ALERT-- Google Maps Autocomplete Error: ${error?.status} ${error?.message}`, error);
}

// Save dragged marker location ===============================================
const draggedMarkerNewLocation = inject("draggedMarkerNewLocation") as Ref<Location>;

watch(draggedMarkerNewLocation, () => {
  // The `isEditMode` check is required to identify the location currently in edit mode and to apply marker position changes exclusively to this location
  if (isEditMode.value) {
    _collabLocation.value = { ..._collabLocation.value, latitude: draggedMarkerNewLocation.value.latitude, longitude: draggedMarkerNewLocation.value.longitude };
  }
});

// "Set the Pin" note visibility ==============================================
const isSetThePinNoteVisible = computed<boolean>(() => {
  const isLatLngSet = _collabLocation.value.latitude !== null && _collabLocation.value.longitude !== null;
  // Also only show this when the user clicks away (not as they're typing in the auto-complete)
  return Boolean(isEditMode.value && _collabLocation.value.title && !isLatLngSet && !isAutocompleteInputFocused.value && !isAddressInputFocused.value);
});

watch(isSetThePinNoteVisible, () => emit("showSetThePinNote", isSetThePinNoteVisible.value));

// Get map click location =====================================================
const mapClickLocation = inject("mapClickLocation") as Ref<Location>;

watch(mapClickLocation, () => {
  if (isSetThePinNoteVisible.value) {
    _collabLocation.value = { ..._collabLocation.value, latitude: mapClickLocation.value.latitude, longitude: mapClickLocation.value.longitude };
  }
});

// Opening hours text =========================================================
const openHoursText = computed<string>(() => {
  if (!_collabLocation.value.openHours || !_collabLocation.value.visitDay) {
    return "";
  }

  const dayOfWeek = moment(_collabLocation.value.visitDay).format("dddd");
  const dayIndex = dayOfWeekIndexes[dayOfWeek];
  let workingHours = _collabLocation.value.openHours[dayIndex];

  if (workingHours.startsWith(dayOfWeek)) {
    workingHours = workingHours.replace(dayOfWeek, "");

    if (workingHours.startsWith(":")) {
      workingHours = workingHours.slice(1).trim();
    }
  }

  if (!workingHours.toLocaleLowerCase().includes("open") && !workingHours.toLocaleLowerCase().includes("closed")) {
    workingHours = "Open " + workingHours;
  }

  return workingHours;
});

// Delete =====================================================================
async function deleteLocation() {
  emit("showSetThePinNote", false);
  emit("deleteLocation", props.index);
}

// Save =======================================================================
async function save() {
  await props.saveLocationFunc(_collabLocation.value.id);

  isAutocompleteInputFocused.value = false;
  isAddressInputFocused.value = false;

  snippetState.value = SnippetStates.ShortSummary;
}

// Add without Google place ===================================================
function addWithoutGooglePlace() {
  isAutocompleteInputFocused.value = false;
  snippetState.value = SnippetStates.FullForm;
  emit("showSetThePinNote", true);
}

// Define expose ==============================================================
defineExpose({
  focusAutocompleteInput: () => autocompleteInput.value.$el.focus(),
  skipEditing: () => {
    if (!props.collabLocation.title) {
      snippetState.value = SnippetStates.ShortForm;
    } else {
      snippetState.value = SnippetStates.ShortSummary;
    }
  },
  showFullForm: () => (snippetState.value = SnippetStates.FullForm),
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Short form =================================================================
.short-form {
  gap: 12px;
  display: flex;
  align-items: center;

  &__location-input {
    content: "";
  }

  &__add-button {
  }
}

// Short summary ==============================================================
.short-summary {
  gap: 3px 12px;
  display: flex;
  align-items: center;

  &__text-content {
    padding: 5px 0;
    gap: 4px 12px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__name {
    color: rgba(0, 0, 0, 1);
    font:
      700 14px/14px "Quicksand",
      sans-serif;
  }

  &__note-and-visit-day {
    gap: 3px 12px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__note {
    max-width: 200px;
    font-style: italic;
    white-space: nowrap;

    display: -webkit-box !important;
    overflow: hidden !important;
    line-clamp: 1 !important;
    -webkit-line-clamp: 1 !important;
    text-overflow: ellipsis !important;
    -webkit-box-orient: vertical !important;
  }

  &__visit-day {
    font-style: italic;
    white-space: nowrap;
  }

  &__edit-button {
    margin-top: 3px;
    align-self: flex-start;
  }
}

// Address row ================================================================
.address-row {
  gap: 8px;
  display: flex;
  align-items: flex-start;

  &__address {
    padding-top: 3px;
  }

  &__change-link {
    content: "";
  }
}

// Visit day row ==============================================================
.visit-day-row {
  gap: 6px 12px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;

  &__title {
    min-width: 55px;
  }

  &__input-date {
    width: 135px;
    min-width: 135px;
  }

  &__working-hours {
    white-space: pre;

    &--red-and-bold {
      font-weight: 600;
      color: rgb(162, 48, 48);
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .visit-day-row {
    flex-wrap: wrap;

    &__working-hours {
      width: 100%;
      padding-left: calc(55px + 12px);
      box-sizing: border-box;
    }
  }
}

// Note row ===================================================================
.note-row {
  gap: 12px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;

  &__title {
    min-width: 55px;
  }

  &__text-input {
    content: "";
  }
}

// Buttons row ================================================================
.buttons-row {
  gap: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__side-left {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__side-right {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__button {
    content: "";
  }
}

// Full form ==================================================================
.full-form {
  gap: 19px;
  display: flex;
  flex-direction: column;

  &__place-name-input {
    content: "";
  }

  &__address-row {
    margin-top: -12px;
  }

  &__address-input {
    margin-top: -12px;
  }

  &__set-the-pin-warning {
    align-self: flex-start;
  }

  &__visit-day-row {
    content: "";
  }

  &__note-row {
    content: "";
  }

  &__buttons-row {
    content: "";
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .full-form {
    &__buttons-row {
      margin-left: -50px;
    }
  }
}

// Location input snippet =====================================================
.location-input-snippet {
  gap: 7px;
  padding: 0;
  display: flex;
  align-items: flex-start;
  position: relative;
  z-index: 0;
  opacity: 1;
  transition:
    padding 0.07s ease-in-out,
    opacity 0.07s ease-in-out;

  &--disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &--error {
    background-color: rgba(255, 0, 0, 0.1);
  }

  &--align-items-center {
    align-items: center;
  }

  &--small-vertical-paddings {
    padding: 4px 0;
  }

  &--big-vertical-paddings-and-gray-bg {
    padding: 17px 0 !important;

    &::after {
      content: "";
      width: calc(100% + 100px);
      height: 100%;
      position: absolute;
      inset: 0 auto auto -50px;
      z-index: -1;
      background: rgba(242, 242, 242, 1);
    }
  }

  // Column left ----------------------
  &__column-left {
    width: 52px;
    min-width: 52px;
    min-height: 41px;
    gap: 0 7px;
    margin-right: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: grab;

    &--disabled {
      pointer-events: none;
    }

    &--smaller-height {
      min-height: 30px;
    }
  }

  &__drag-n-drop-handle {
    user-select: none;
    pointer-events: none;

    &--disabled {
      opacity: 0.2;
    }
  }

  &__order-number {
    color: rgba(0, 0, 0, 1);
    font:
      600 18px/18px "Quicksand",
      sans-serif;
    pointer-events: none;
  }

  // Column right ---------------------
  &__column-right {
    max-width: calc(100% - 20px);
    min-height: 41px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    &--limited-width {
      width: 390px;
    }

    &--smaller-height {
      min-height: 30px;
    }
  }

  &__short-summary {
    content: "";
  }

  &__short-form {
    content: "";
  }

  &__full-form {
    content: "";
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-input-snippet {
    &__column-left {
      margin-left: -15px;
    }
  }
}
</style>
